/**
 * @fileoverview Excel (XLSX) file generation and export utilities.
 *
 * Provides functionality to export JavaScript data to password-protected
 * Excel files using the xlsx-populate library.
 *
 * @module helpers/xlsxHelper
 */

import _ from "lodash";
import * as Sentry from "@sentry/browser";

const XlsxPopulate = require("xlsx-populate");

/**
 * Converts an array of objects to a 2D array suitable for Excel export.
 *
 * The first row contains headers (object keys), and subsequent rows
 * contain the corresponding values.
 *
 * @param {Object[]} obj - Array of objects to convert
 * @returns {string[][]} 2D array with headers in first row
 *
 * @example
 * convertObjectTo2DArray([
 *   { name: 'John', age: 30 },
 *   { name: 'Jane', age: 25 }
 * ]);
 * // Returns: [['name', 'age'], ['John', 30], ['Jane', 25]]
 */
function convertObjectTo2DArray(obj: any[]): string[][] {
  if (!Array.isArray(obj) || obj.length === 0) {
    return [];
  }
  const result: string[][] = [[]];
  _.forOwn(obj[0], (_, key) => {
    result[0].push(key);
  });

  _.forEach(obj, (value, idx) => {
    result[idx + 1] = [];
    _.forEach(result[0], (key) => {
      result[idx + 1].push(value[key]);
    });
  });
  return result;
}

/**
 * Exports an array of objects to a password-protected XLSX file.
 *
 * Converts the data to Excel format and triggers a browser download.
 *
 * @param {Object[]} data - Array of objects to export
 * @param {string} [fileName=''] - Name for the downloaded file
 * @param {string} password - Password to protect the Excel file
 * @returns {void}
 *
 * @example
 * exportToXLSX(
 *   [{ name: 'John', email: 'john@example.com' }],
 *   'users.xlsx',
 *   'secretPassword'
 * );
 */
export const exportToXLSX = (data: any[], fileName: string = "", password: string) => {
  if (!Array.isArray(data) || data.length === 0) {
    return;
  }
  const convertedData = convertObjectTo2DArray(data);
  generateBlob(convertedData, fileName, password);
};

/**
 * Generates an Excel blob and triggers download.
 *
 * Creates a new Excel workbook, populates it with data, applies password
 * protection, and initiates the browser download. Supports both modern
 * browsers and IE/Edge legacy.
 *
 * @param {string[][]} data - 2D array of cell values
 * @param {string} fileName - Name for the downloaded file
 * @param {string} password - Password for file protection
 * @returns {Promise<void>}
 */
function generateBlob(data: string[][], fileName: string, password: string) {
  return XlsxPopulate.fromBlankAsync()
    .then((workbook: any) => {
      workbook.sheet(0).cell("A1").value(data);
      return workbook.outputAsync({ password: password });
    })
    .then((blob: any) => {
      const nav = window.navigator as any;
      // IE/Edge legacy support
      if (nav && nav.msSaveOrOpenBlob) {
        nav.msSaveOrOpenBlob(blob, fileName);
      } else {
        // Modern browsers
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    })
    .catch((err: any) => {
      Sentry.captureMessage(`Encountered error on generateBlob: ${err.message || err}`);
    });
}
